import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useAdmissions,
  useAdmissionsPaginated,
  useBulkDeleteAdmissions,
  useDeleteAdmission,
  useCancelAdmission,
  useDischargeAdmission,
  useBeds,
  useWards,
  useDoctors,
  useMoveToRiskFund,
} from '../../services/httpServices/queries';
import { toast } from 'sonner';
import { useDebounce } from '../../hooks/useDebounce';
import { usePermissions } from '../../hooks/usePermissions';
import { ipdService, type AdmissionBillingStatus } from '../../services/httpServices/ipdService';
import { Pagination } from '../../components/ui/Pagination';
import { DatePicker } from '../../components/ui/DatePicker';
import { joinFullName } from '../../utils/patientName';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { RiskFundMoveDialog } from '../../components/ui/RiskFundMoveDialog';
import { AdmissionPaymentBadge } from '../../components/ui/admission-payment-badge';
import {
  endsVisitInDetailsModal,
  isCancelledAdmission,
  isEmergencyAdmission,
  isOpenStayStatus,
} from '../../utils/admission';
import { IpdAdmissionDetailsModal } from './IpdAdmissionDetailsModal';
import { formatOrdinalDate, formatOrdinalDateTimeLong } from '../../utils/dateFormat';
import {
  Bed,
  Loader2,
  AlertCircle,
  AlertTriangle,
  Plus,
  Search,
  CheckCircle,
  User,
  Calendar,
  LayoutGrid,
  List,
  BedDouble,
  X,
  Stethoscope,
  FileText,
  Hash,
  MapPin,
  Banknote,
  ClipboardList,
  ArrowUpRight,
  Activity,
  Proportions,
  Trash2,
  Square,
  SquareCheck,
  SlidersHorizontal,
  Ban,
  UserPlus,
} from 'lucide-react';

const statusOptions = [
  { value: '', label: 'All status' },
  { value: 'ADMITTED', label: 'Admitted' },
  { value: 'DISCHARGE_INITIATED', label: 'Discharge initiated' },
  { value: 'DISCHARGED', label: 'Discharged' },
  { value: 'TRANSFERRED', label: 'Transferred' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const paymentOptions = [
  { value: '', label: 'All payments' },
  { value: 'DUE', label: 'Due' },
  { value: 'PARTIAL', label: 'Partial (unbilled)' },
  { value: 'OVERDUE', label: 'Overdue' },
  { value: 'PAID', label: 'Paid' },
  { value: 'RISK_FUND', label: 'Risk Fund' },
  { value: 'NO_INVOICE', label: 'Not billed' },
];

/** Shared look for the list-view filter controls; an active filter is tinted. */
const filterControl = (active: boolean) =>
  `px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary transition-colors ${
    active
      ? 'border-primary bg-primary/10 text-primary font-medium'
      : 'border-border bg-background text-text'
  }`;

const money = (value: number) =>
  `৳${(Number.isFinite(value) ? value : 0).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  })}`;

export function Ipd() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const limit = 20;

  const [searchQuery, setSearchQuery] = useState(() => params.get('search') || '');
  const [selectedStatus, setSelectedStatus] = useState<string>(() => params.get('status') || '');
  const [viewMode, setViewMode] = useState<'list' | 'board'>(() => (params.get('view') as 'list' | 'board') || 'list');
  const [selectedBedId, setSelectedBedId] = useState<string | null>(null);
  const [page, setPage] = useState(() => {
    const p = params.get('page');
    return p ? Math.max(1, parseInt(p, 10)) : 1;
  });

  // List filters (API based)
  const [paymentStatus, setPaymentStatus] = useState(() => params.get('paymentStatus') || '');
  const [wardId, setWardId] = useState(() => params.get('wardId') || '');
  const [doctorId, setDoctorId] = useState(() => params.get('doctorId') || '');
  const [from, setFrom] = useState(() => params.get('from') || '');
  const [to, setTo] = useState(() => params.get('to') || '');
  // Which date the from/to window means: the admission date (default) or the
  // day the visit's invoices were fully settled ("Settled On" column).
  const [dateBasis, setDateBasis] = useState<'admitted' | 'settled'>(() =>
    params.get('dateBasis') === 'settled' ? 'settled' : 'admitted',
  );
  // Ward / doctor / date range are occasional filters, so they stay collapsed.
  const [showMoreFilters, setShowMoreFilters] = useState(
    () =>
      Boolean(
        params.get('wardId') ||
          params.get('doctorId') ||
          params.get('from') ||
          params.get('to') ||
          params.get('dateBasis'),
      ),
  );

  // Row selection & popup
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [riskFundConfirmOpen, setRiskFundConfirmOpen] = useState(false);
  const [riskFundReason, setRiskFundReason] = useState('');
  const [riskFundError, setRiskFundError] = useState<string | null>(null);
  const [admissionAction, setAdmissionAction] = useState<{
    type: 'delete' | 'cancel';
    admission: any;
  } | null>(null);
  // Billed discharged admissions (paid or due) open a compact details modal
  // (with print + pay actions) instead of navigating to the full admission page.
  const [detailAdmission, setDetailAdmission] = useState<any | null>(null);

  const debouncedSearch = useDebounce(searchQuery, 500);

  const listFilters = useMemo(() => {
    const filters: Record<string, string> = {};
    if (debouncedSearch.length >= 2) filters.search = debouncedSearch;
    if (selectedStatus) filters.status = selectedStatus;
    if (paymentStatus === 'OVERDUE') {
      filters.dueStatus = 'overdue';
    } else if (paymentStatus) {
      filters.paymentStatus = paymentStatus;
    }
    if (wardId) filters.wardId = wardId;
    if (doctorId) filters.doctorId = doctorId;
    if (from) filters.from = from;
    if (to) filters.to = to;
    if ((from || to) && dateBasis === 'settled') filters.dateBasis = 'settled';
    return filters;
  }, [debouncedSearch, selectedStatus, paymentStatus, wardId, doctorId, from, to, dateBasis]);

  const boardQuery = useAdmissions(selectedStatus || undefined, { enabled: viewMode === 'board' });
  const listQuery = useAdmissionsPaginated(page, limit, listFilters);
  const { data: bedsData, isLoading: bedsLoading, error: bedsError } = useBeds();
  const { data: wardsData, isLoading: wardsLoading, error: wardsError } = useWards(1, 100);
  const { data: doctorsData } = useDoctors(1, 200);
  const dischargeAdmission = useDischargeAdmission();
  const deleteAdmission = useDeleteAdmission();
  const cancelAdmission = useCancelAdmission();
  const bulkDeleteAdmissions = useBulkDeleteAdmissions();
  const moveToRiskFund = useMoveToRiskFund();
  const { hasAccess, hasPermission } = usePermissions();
  const canBill = hasAccess('/billing/invoices');
  // Sensitive finance action: admins and custom roles granted the capability.
  const canManageRiskFund = hasPermission('billing:risk-fund:manage');

  /**
   * Cancelling a stay that still owes money writes the balance off to the Risk
   * Fund, so it needs `billing:risk-fund:manage`. A role without it can still
   * cancel a fully settled stay — there is nothing to write off. Mirrors the
   * server-side gate in `IpdService.cancelAdmission`.
   */
  const canCancelAdmission = (admission: any) =>
    canManageRiskFund || Number(admission?.dueAmount || 0) <= 0;

  // Set when a discharge is blocked by the bill; drives the billing modal. The
  // modal has two modes: charges still unbilled (hard block) or the bill is
  // invoiced but a balance is unpaid (discharge with a confirmed due).
  const [billingGate, setBillingGate] = useState<{
    id: string;
    admission: any;
    status: AdmissionBillingStatus;
  } | null>(null);
  // Promise date / note recorded when a discharge carries a balance.
  const [dueDate, setDueDate] = useState('');
  const [dueNote, setDueNote] = useState('');

  const gateNeedsInvoice = billingGate ? !billingGate.status.canDischarge : false;
  const gateDueAmount = billingGate?.status.outstandingDue ?? 0;

  const listAdmissions = listQuery.data?.data || [];
  const listMeta = listQuery.data?.meta;
  const boardAdmissions = boardQuery.data?.data || [];
  const beds = bedsData?.data || [];
  const wards = wardsData?.data || [];
  const doctors = doctorsData?.data || [];

  const isLoading =
    viewMode === 'board'
      ? boardQuery.isLoading || bedsLoading || wardsLoading
      : listQuery.isLoading;
  const error =
    viewMode === 'board'
      ? boardQuery.error || bedsError || wardsError
      : listQuery.error;

  const getPatientName = (patient: any) => {
    if (!patient) return 'Unknown';
    return joinFullName(patient) || patient.registrationNumber || 'Unknown';
  };
  const getDoctorName = (doctor: any) => doctor?.name || 'Unknown';
  const getBedName = (bed: any) => bed?.bedNumber || 'No bed';
  const getWardName = (bed: any) => bed?.wardEntity?.wardName || bed?.ward || '—';
  const getAdmittedAt = (admission: any) => admission?.admittedAt || admission?.admissionDate;
  const getReason = (admission: any) => admission?.diagnosis || admission?.admissionReason || 'N/A';

  // Two separate concepts — do not merge them:
  //  • Referral partner → the payee of a flat ৳ commission. Only exists when a
  //    partner is linked (`referralId`), independent of `admissionType`.
  //  • Referred by → the doctor/clinic who sent the patient. Clinical
  //    attribution only; it never creates a commission or a payout.
  const hasReferralPartner = (admission: any) =>
    Boolean(admission?.referralId || admission?.referral?.id);

  const getReferralPartnerName = (admission: any) =>
    admission?.referral?.name || admission?.referral?.organization || null;

  /** "Ayesha Siddiqa — Consultant, Medicine" for tooltips/sub-lines. */
  const getReferralPartnerLabel = (admission: any) => {
    const referral = admission?.referral;
    const name = getReferralPartnerName(admission);
    if (!name) return null;
    const detail = [referral?.designation, referral?.specialization, referral?.organization]
      .filter(Boolean)
      .join(', ');
    return detail && detail !== name ? `${name} — ${detail}` : name;
  };

  const getReferralCommission = (admission: any) =>
    Math.max(0, Math.round(Number(admission?.referralFlatAmount) || 0));

  /** Referring doctor — a clinical attribution, not a commission. */
  const getReferredByLabel = (admission: any) =>
    admission?.referredBy?.name ||
    doctors.find((doctor: any) => doctor.id === admission?.referredById)?.name ||
    null;

  /** Free-text "referred from" note carried by legacy referral admissions. */
  const getReferredFromLabel = (admission: any) => admission?.referralDetails || null;

  /**
   * Discharges a patient. Any unpaid balance blocks the first attempt so the
   * staff member has to confirm it explicitly; the confirmation carries the
   * balance on the invoices and records the amount on the admission.
   */
  const handleDischarge = async (
    id: string,
    options: {
      allowDue?: boolean;
      dischargeDueDate?: string;
      dischargeDueNote?: string;
    } = {},
  ) => {
    const admission =
      listAdmissions.find((a: any) => a.id === id) ||
      boardAdmissions.find((a: any) => a.id === id) ||
      null;

    try {
      await dischargeAdmission.mutateAsync({ id, data: options });
      toast.success(
        options.allowDue
          ? 'Patient discharged — balance carried as patient due'
          : 'Patient discharged',
      );
      setBillingGate(null);
      return;
    } catch (err: any) {
      // A blocked discharge is a billing gate. Re-read the bill: unbilled
      // charges block hard, an unpaid balance is one confirmation away.
      try {
        const res = await ipdService.getAdmissionBillingStatus(id);
        const status = res.data;
        if (status && (!status.canDischarge || status.hasOutstandingDue)) {
          setBillingGate({ id, admission, status });
          setDueDate('');
          setDueNote('');
          return;
        }
      } catch {
        // Fall through to the generic error message below.
      }
      toast.error(
        err?.response?.data?.message || err?.message || 'Failed to discharge patient',
      );
    }
  };

  // Open the dedicated admission page, remembering the current list URL so
  // "Back to IPD" restores filters/page exactly.
  const openAdmission = (admissionId: string) => {
    navigate(`/ipd/admissions/${admissionId}`, {
      state: { from: `${location.pathname}${location.search}` },
    });
  };

  // Every closed admission (discharged or cancelled) opens the compact,
  // read-only details modal instead of the full admission page — billed or not,
  // the row has already left the ward, so the visit summary is what staff
  // usually want. Cancelled stays never reach a discharge, so this is the only
  // way to review their timeline, billing and receipt.
  const showsDetailsModal = (admission: any) => endsVisitInDetailsModal(admission);

  // Build a map of bedId -> active admission for the board view. A bed transfer
  // re-labels the stay as TRANSFERRED while the patient is still in the ward, so
  // every open status must keep the bed card showing its occupant.
  const bedAdmissionMap = useMemo(() => {
    const map = new Map<string, any>();
    for (const a of boardAdmissions) {
      if (a.bedId && isOpenStayStatus(a.status)) {
        map.set(a.bedId, a);
      }
    }
    return map;
  }, [boardAdmissions]);

  // Group beds by ward name
  const bedsByWard = useMemo(() => {
    const map = new Map<string, any[]>();
    for (const bed of beds) {
      const wardName = bed.ward || 'Unassigned';
      if (!map.has(wardName)) map.set(wardName, []);
      map.get(wardName)!.push(bed);
    }
    for (const [, list] of map) {
      list.sort((a, b) => (a.bedNumber || '').localeCompare(b.bedNumber || '', undefined, { numeric: true }));
    }
    return new Map([...map.entries()].sort((a, b) => a[0].localeCompare(b[0])));
  }, [beds]);

  // Overall stats
  const stats = useMemo(() => {
    const total = beds.length;
    const occupied = beds.filter((b: any) => b.status === 'OCCUPIED').length;
    const available = beds.filter((b: any) => b.status === 'AVAILABLE').length;
    const reserved = beds.filter((b: any) => b.status === 'RESERVED').length;
    const maintenance = beds.filter((b: any) => b.status === 'MAINTENANCE').length;
    const occupancyRate = total > 0 ? Math.round((occupied / total) * 100) : 0;
    return { total, occupied, available, reserved, maintenance, occupancyRate };
  }, [beds]);

  const selectedBed = selectedBedId ? beds.find((b: any) => b.id === selectedBedId) || null : null;
  const selectedAdmissionForBed = selectedBedId ? bedAdmissionMap.get(selectedBedId) || null : null;

  const closeModal = () => setSelectedBedId(null);

  const statusMeta: Record<string, { label: string; color: string; bg: string; border: string; lightBg: string; dot: string }> = {
    AVAILABLE: {
      label: 'Available',
      color: 'text-emerald-700',
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      lightBg: 'bg-emerald-500',
      dot: 'bg-emerald-500',
    },
    OCCUPIED: {
      label: 'Occupied',
      color: 'text-rose-700',
      bg: 'bg-rose-50',
      border: 'border-rose-200',
      lightBg: 'bg-rose-500',
      dot: 'bg-rose-500',
    },
    RESERVED: {
      label: 'Reserved',
      color: 'text-amber-700',
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      lightBg: 'bg-amber-500',
      dot: 'bg-amber-500',
    },
    MAINTENANCE: {
      label: 'Maintenance',
      color: 'text-slate-600',
      bg: 'bg-slate-100',
      border: 'border-slate-200',
      lightBg: 'bg-slate-400',
      dot: 'bg-slate-400',
    },
  };

  const getStatusMeta = (status: string) => statusMeta[status] || statusMeta.AVAILABLE;

  const admissionStatusClass = (status: string) =>
    status === 'ADMITTED'
      ? 'bg-primary/10 text-primary'
      : status === 'DISCHARGED'
        ? 'bg-emerald-100 text-emerald-700'
        : status === 'DISCHARGE_INITIATED'
          ? 'bg-amber-100 text-amber-700'
          : status === 'CANCELLED'
            ? 'bg-rose-100 text-rose-700'
            : 'bg-amber-100 text-amber-700';

  // Selection helpers
  const allSelected = listAdmissions.length > 0 && listAdmissions.every((a: any) => selectedIds.includes(a.id));
  const someSelected = selectedIds.length > 0;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !listAdmissions.some((a: any) => a.id === id)));
    } else {
      const newIds = listAdmissions.map((a: any) => a.id);
      setSelectedIds((prev) => Array.from(new Set([...prev, ...newIds])));
    }
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    try {
      const res: any = await bulkDeleteAdmissions.mutateAsync(ids);
      const deletedIds: string[] = res?.data?.deleted ?? ids;
      // Stays that already took payment are refused by the API: deleting them
      // would erase the encounter their collected money belongs to.
      const blockedIds: string[] = res?.data?.blocked ?? [];
      setSelectedIds(blockedIds);
      setBulkConfirmOpen(false);
      if (deletedIds.length > 0) {
        toast.success(
          `Deleted ${deletedIds.length} admission${deletedIds.length === 1 ? '' : 's'}`,
        );
      }
      if (blockedIds.length > 0) {
        toast.warning(
          `${blockedIds.length} admission${blockedIds.length === 1 ? '' : 's'} not deleted — money has already been collected. Cancel them instead so the payment history stays.`,
        );
      }
      if (page > 1 && deletedIds.length >= listAdmissions.length) {
        setPage((p) => Math.max(1, p - 1));
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to delete admissions');
    }
  };

  const handleBulkRiskFund = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    const reason = riskFundReason.trim();
    if (!reason) {
      setRiskFundError('Enter a reason for moving the balances to the Risk Fund.');
      return;
    }
    setRiskFundError(null);
    try {
      const res: any = await moveToRiskFund.mutateAsync({
        admissionIds: ids,
        reason,
      });
      const movedCount = res?.data?.moved?.length ?? 0;
      const skipped = res?.data?.skipped ?? [];
      const totalAmount = Number(res?.data?.totalAmount) || 0;

      if (movedCount === 0) {
        setRiskFundError(
          skipped[0]?.reason ||
            'None of the selected admissions had an outstanding invoice to move.',
        );
        return;
      }

      toast.success(
        `Moved ${movedCount} admission${movedCount === 1 ? '' : 's'} to the Risk Fund (৳${totalAmount.toLocaleString()})`,
      );
      if (skipped.length > 0) {
        toast.warning(
          `${skipped.length} admission${skipped.length === 1 ? ' was' : 's were'} skipped: ${skipped[0]?.reason}`,
        );
      }
      setSelectedIds([]);
      setRiskFundConfirmOpen(false);
      setRiskFundReason('');
      if (page > 1 && ids.length >= listAdmissions.length) {
        setPage((p) => Math.max(1, p - 1));
      }
    } catch (err: any) {
      setRiskFundError(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to move admissions to Risk Fund',
      );
    }
  };

  const handleAdmissionAction = async () => {
    const action = admissionAction;
    if (!action) return;

    const verb = action.type === 'delete' ? 'delete' : 'cancel';
    try {
      if (action.type === 'delete') {
        await deleteAdmission.mutateAsync(action.admission.id);
        toast.success('Admission deleted');
        setSelectedIds((prev) => prev.filter((id) => id !== action.admission.id));
        if (page > 1 && listAdmissions.length === 1) {
          setPage((p) => Math.max(1, p - 1));
        }
      } else {
        await cancelAdmission.mutateAsync(action.admission.id);
        // Cancelling writes any unpaid bill off to the Risk Fund, so say so
        // instead of claiming a plain status change.
        const writtenOff = Number(action.admission.dueAmount || 0);
        toast.success(
          writtenOff > 0
            ? `Admission cancelled — ${money(writtenOff)} moved to the Risk Fund`
            : 'Admission cancelled',
        );
        setSelectedIds((prev) => prev.filter((id) => id !== action.admission.id));
      }
      setAdmissionAction(null);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          `Failed to ${verb} admission`,
      );
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedStatus('');
    setPaymentStatus('');
    setWardId('');
    setDoctorId('');
    setFrom('');
    setTo('');
    setDateBasis('admitted');
    setPage(1);
  };

  const hasActiveFilters = Boolean(
    searchQuery || selectedStatus || paymentStatus || wardId || doctorId || from || to,
  );
  const hasMoreFilters = Boolean(wardId || doctorId || from || to);

  // Sync URL params (search is debounced so the URL isn't rewritten per keystroke)
  useEffect(() => {
    const newParams = new URLSearchParams();
    if (debouncedSearch) newParams.set('search', debouncedSearch);
    if (selectedStatus) newParams.set('status', selectedStatus);
    if (viewMode !== 'board') newParams.set('view', viewMode);
    if (viewMode !== 'board' && page > 1) newParams.set('page', String(page));
    if (viewMode !== 'board' && paymentStatus) newParams.set('paymentStatus', paymentStatus);
    if (viewMode !== 'board' && wardId) newParams.set('wardId', wardId);
    if (viewMode !== 'board' && doctorId) newParams.set('doctorId', doctorId);
    if (viewMode !== 'board' && from) newParams.set('from', from);
    if (viewMode !== 'board' && to) newParams.set('to', to);
    if (viewMode !== 'board' && (from || to) && dateBasis === 'settled') {
      newParams.set('dateBasis', 'settled');
    }
    const query = newParams.toString();
    const newUrl = `${location.pathname}${query ? `?${query}` : ''}`;
    if (newUrl !== `${location.pathname}${location.search}`) {
      navigate(newUrl, { replace: true });
    }
  }, [
    debouncedSearch,
    selectedStatus,
    viewMode,
    page,
    paymentStatus,
    wardId,
    doctorId,
    from,
    to,
    dateBasis,
    navigate,
    location.pathname,
    location.search,
  ]);

  // Clear selection whenever the visible page or filters change.
  useEffect(() => {
    setSelectedIds([]);
  }, [page, listFilters]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Bed className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-text tracking-tight">IPD Management</h1>
              <p className="text-sm text-text-muted">Wards, beds & admissions</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center bg-surface border border-border rounded-xl p-1 shadow-sm">
            <button
              onClick={() => setViewMode('board')}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg flex items-center gap-2 transition-all ${
                viewMode === 'board'
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-text-muted hover:text-text hover:bg-background'
              }`}
            >
              <LayoutGrid size={15} />
              Bed Board
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg flex items-center gap-2 transition-all ${
                viewMode === 'list'
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-text-muted hover:text-text hover:bg-background'
              }`}
            >
              <List size={15} />
              List
            </button>
          </div>
          <button
            onClick={() => navigate('/ipd/new')}
            className="px-4 py-2.5 bg-primary hover:bg-primary-dark text-white rounded-xl transition-all text-sm font-semibold flex items-center gap-2 shadow-sm hover:shadow"
          >
            <Plus size={16} strokeWidth={2.5} />
            New Admission
          </button>
        </div>
      </div>

      {/* Stats */}
      {viewMode === 'board' && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
          <div className="bg-surface border border-border rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">Total Beds</span>
              <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                <BedDouble size={14} className="text-primary" />
              </div>
            </div>
            <p className="text-2xl font-bold text-text">{stats.total}</p>
          </div>
          <div className="bg-surface border border-border rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">Occupied</span>
              <div className="w-7 h-7 rounded-lg bg-rose-100 flex items-center justify-center">
                <Activity size={14} className="text-rose-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-rose-600">{stats.occupied}</p>
          </div>
          <div className="bg-surface border border-border rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">Available</span>
              <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle size={14} className="text-emerald-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-emerald-600">{stats.available}</p>
          </div>
          <div className="bg-surface border border-border rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">Occupancy</span>
              <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center">
                <Proportions size={14} className="text-amber-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-amber-600">{stats.occupancyRate}%</p>
          </div>
          <div className="bg-surface border border-border rounded-xl p-4 shadow-sm col-span-2 sm:col-span-4 lg:col-span-1">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">Other</span>
              <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center">
                <ClipboardList size={14} className="text-slate-500" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <p className="text-lg font-bold text-text">{stats.reserved}</p>
              <span className="text-[10px] text-text-muted uppercase">Reserved</span>
              <span className="text-border">|</span>
              <p className="text-lg font-bold text-text">{stats.maintenance}</p>
              <span className="text-[10px] text-text-muted uppercase">Maint.</span>
            </div>
          </div>
        </div>
      )}

      {/* Search & Filters */}
      {viewMode === 'board' ? (
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search beds, wards, patients..."
              className="w-full pl-10 pr-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all shadow-sm"
            />
          </div>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-3 py-2.5 border border-border rounded-xl bg-surface text-sm text-text shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          >
            {statusOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border p-3 space-y-2">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                placeholder="Search patient, mobile, reg. no., bed, doctor or referral..."
                className="w-full pl-10 pr-9 py-2.5 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {listQuery.isFetching && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-text-muted" />
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <select
                value={selectedStatus}
                onChange={(e) => { setSelectedStatus(e.target.value); setPage(1); }}
                aria-label="Filter by admission status"
                className={filterControl(Boolean(selectedStatus))}
              >
                {statusOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>

              <select
                value={paymentStatus}
                onChange={(e) => { setPaymentStatus(e.target.value); setPage(1); }}
                aria-label="Filter by payment status"
                className={filterControl(Boolean(paymentStatus))}
              >
                {paymentOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>

              <button
                type="button"
                onClick={() => setShowMoreFilters((s) => !s)}
                aria-expanded={showMoreFilters}
                className={`px-3 py-2.5 rounded-lg border text-sm flex items-center gap-1.5 whitespace-nowrap transition-colors ${
                  showMoreFilters || hasMoreFilters
                    ? 'border-primary bg-primary/5 text-primary font-medium'
                    : 'border-border text-text-muted hover:text-text'
                }`}
              >
                <SlidersHorizontal size={15} />
                {showMoreFilters ? 'Fewer filters' : 'More filters'}
                {hasMoreFilters && <span className="w-1.5 h-1.5 rounded-full bg-primary" />}
              </button>

              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="px-2.5 py-2.5 text-text-muted hover:text-danger rounded-lg text-sm flex items-center gap-1 whitespace-nowrap transition-colors"
                >
                  <X size={14} />
                  Clear
                </button>
              )}
            </div>
          </div>

          {showMoreFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 pt-2 border-t border-border">
              <select
                value={wardId}
                onChange={(e) => { setWardId(e.target.value); setPage(1); }}
                aria-label="Filter by ward"
                className={`w-full ${filterControl(Boolean(wardId))}`}
              >
                <option value="">All wards</option>
                {wards.map((w: any) => (
                  <option key={w.id} value={w.id}>{w.wardName}</option>
                ))}
              </select>

              <select
                value={doctorId}
                onChange={(e) => { setDoctorId(e.target.value); setPage(1); }}
                aria-label="Filter by doctor"
                className={`w-full ${filterControl(Boolean(doctorId))}`}
              >
                <option value="">All doctors</option>
                {doctors.map((d: any) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>

              <select
                value={dateBasis}
                onChange={(e) => { setDateBasis(e.target.value as 'admitted' | 'settled'); setPage(1); }}
                aria-label="Date filter basis"
                title="Which date the range applies to"
                className={`w-full ${filterControl(dateBasis === 'settled')}`}
              >
                <option value="admitted">Admitted Date</option>
                <option value="settled">Settled On</option>
              </select>

              <DatePicker
                value={from}
                onChange={(value) => { setFrom(value); setPage(1); }}
                max={to || undefined}
                placeholder={dateBasis === 'settled' ? 'Settled from' : 'Admitted from'}
                aria-label={dateBasis === 'settled' ? 'Settled from' : 'Admitted from'}
                className="py-2.5"
              />

              <DatePicker
                value={to}
                onChange={(value) => { setTo(value); setPage(1); }}
                min={from || undefined}
                placeholder={dateBasis === 'settled' ? 'Settled to' : 'Admitted to'}
                aria-label={dateBasis === 'settled' ? 'Settled to' : 'Admitted to'}
                className="py-2.5"
              />
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-xl text-danger">
          <AlertCircle size={18} />
          <span className="text-sm font-medium">Failed to load data</span>
        </div>
      )}

      {/* Bulk Actions */}
      {viewMode === 'list' && someSelected && (
        <div className="flex items-center gap-3 bg-surface border border-border rounded-xl p-3">
          <span className="text-sm text-text-muted">
            <span className="font-semibold text-text">{selectedIds.length}</span> selected
          </span>
          {canManageRiskFund && (
            <button
              onClick={() => setRiskFundConfirmOpen(true)}
              className="px-3 py-1.5 rounded-lg bg-warning text-white text-xs font-medium hover:bg-warning/90 disabled:opacity-50 flex items-center gap-1.5"
            >
              <AlertTriangle size={12} />
              Move to Risk Fund
            </button>
          )}
          <button
            onClick={() => setBulkConfirmOpen(true)}
            disabled={bulkDeleteAdmissions.isPending}
            className="px-3 py-1.5 rounded-lg bg-danger text-white text-xs font-medium hover:bg-danger/90 disabled:opacity-50 flex items-center gap-1.5"
          >
            {bulkDeleteAdmissions.isPending ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Trash2 size={12} />
            )}
            Delete Selected
          </button>
          <button
            onClick={() => setSelectedIds([])}
            className="px-3 py-1.5 rounded-lg border border-border text-text-muted text-xs font-medium hover:text-text transition-colors"
          >
            Clear Selection
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : viewMode === 'board' ? (
        /* ===== BED BOARD VIEW ===== */
        <div className="space-y-6">
          {bedsByWard.size === 0 ? (
            <div className="bg-surface rounded-2xl border border-border p-12 text-center shadow-sm">
              <div className="w-16 h-16 rounded-2xl bg-background flex items-center justify-center mx-auto mb-4">
                <BedDouble className="w-8 h-8 text-text-muted/40" />
              </div>
              <p className="text-lg font-semibold text-text">No beds configured</p>
              <p className="text-sm text-text-muted mt-1">Add beds in the Beds section to see them here.</p>
            </div>
          ) : (
            Array.from(bedsByWard.entries()).map(([wardName, wardBeds]) => {
              const wardInfo = wards.find((w: any) => w.wardName === wardName);
              const occupiedCount = wardBeds.filter((b: any) => b.status === 'OCCUPIED').length;
              const availableCount = wardBeds.filter((b: any) => b.status === 'AVAILABLE').length;
              const reservedCount = wardBeds.filter((b: any) => b.status === 'RESERVED').length;
              const maintCount = wardBeds.filter((b: any) => b.status === 'MAINTENANCE').length;
              const rate = wardBeds.length > 0 ? Math.round((occupiedCount / wardBeds.length) * 100) : 0;

              return (
                <div key={wardName} className="bg-surface rounded-2xl border border-border shadow-sm overflow-hidden">
                  {/* Ward Header */}
                  <div className="px-5 py-4 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-background/40">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <BedDouble size={18} className="text-primary" />
                      </div>
                      <div>
                        <h2 className="text-base font-bold text-text">{wardName}</h2>
                        <div className="flex items-center gap-2 text-xs text-text-muted mt-0.5">
                          {wardInfo?.floor && <span>Floor {wardInfo.floor}</span>}
                          {wardInfo?.floor && <span className="text-border">|</span>}
                          <span>{wardBeds.length} beds</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-5">
                      {/* Mini progress bar */}
                      <div className="hidden md:flex items-center gap-2">
                        <div className="w-24 h-2 rounded-full bg-background overflow-hidden flex">
                          <div className="h-full bg-emerald-500" style={{ width: `${(availableCount / wardBeds.length) * 100}%` }} />
                          <div className="h-full bg-rose-500" style={{ width: `${(occupiedCount / wardBeds.length) * 100}%` }} />
                          <div className="h-full bg-amber-400" style={{ width: `${(reservedCount / wardBeds.length) * 100}%` }} />
                          <div className="h-full bg-slate-300" style={{ width: `${(maintCount / wardBeds.length) * 100}%` }} />
                        </div>
                        <span className="text-xs font-semibold text-text-muted">{rate}% full</span>
                      </div>

                      <div className="flex items-center gap-3 text-xs">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-full bg-emerald-500" />
                          <span className="text-text-muted font-medium">{availableCount}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-full bg-rose-500" />
                          <span className="text-text-muted font-medium">{occupiedCount}</span>
                        </div>
                        {reservedCount > 0 && (
                          <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-full bg-amber-400" />
                            <span className="text-text-muted font-medium">{reservedCount}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Bed Grid */}
                  <div className="p-5">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 2xl:grid-cols-10 gap-3">
                      {wardBeds.map((bed: any) => {
                        const admission = bedAdmissionMap.get(bed.id);
                        const meta = getStatusMeta(bed.status);
                        const isOccupied = bed.status === 'OCCUPIED';

                        return (
                          <button
                            key={bed.id}
                            onClick={() => setSelectedBedId(bed.id)}
                            className={`group relative text-left rounded-2xl border p-3.5 flex flex-col gap-2.5 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-primary/30 ${meta.bg} ${meta.border}`}
                          >
                            {/* Top row */}
                            <div className="flex items-center justify-between">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-white/80 shadow-sm`}>
                                <BedDouble size={16} className={meta.color} />
                              </div>
                              <div className={`w-2 h-2 rounded-full ${meta.dot} ring-2 ring-white`} />
                            </div>

                            {/* Content */}
                            <div>
                              <p className={`text-sm font-bold ${meta.color} leading-tight`}>{bed.bedNumber}</p>
                              {isOccupied && admission ? (
                                <p
                                  className="text-[11px] text-text font-medium truncate mt-0.5 leading-tight"
                                  title={[
                                    getPatientName(admission.patient),
                                    hasReferralPartner(admission)
                                      ? `Referral commission: ${
                                          getReferralPartnerName(admission) || 'partner'
                                        }${
                                          getReferralCommission(admission) > 0
                                            ? ` (${money(getReferralCommission(admission))})`
                                            : ' (no amount set)'
                                        }`
                                      : null,
                                    getReferredByLabel(admission)
                                      ? `Referred by: ${getReferredByLabel(admission)}`
                                      : null,
                                    getReferredFromLabel(admission)
                                      ? `Referred from: ${getReferredFromLabel(admission)}`
                                      : null,
                                  ]
                                    .filter(Boolean)
                                    .join(' — ')}
                                >
                                  {hasReferralPartner(admission) && (
                                    <UserPlus
                                      size={10}
                                      className="mr-1 inline shrink-0 align-[-1px] text-primary"
                                      aria-label="Referral commission"
                                    />
                                  )}
                                  {getPatientName(admission.patient)}
                                </p>
                              ) : (
                                <p className={`text-[11px] ${meta.color} opacity-70 mt-0.5 leading-tight`}>{meta.label}</p>
                              )}
                            </div>

                            {/* Hover arrow */}
                            <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                              <ArrowUpRight size={14} className="text-text-muted" />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      ) : (
        /* ===== LIST VIEW ===== */
        <div className="bg-surface rounded-2xl border border-border shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3.5 w-10">
                    <button
                      onClick={toggleSelectAll}
                      className="text-text-muted hover:text-text transition-colors"
                      aria-label="Select all admissions"
                    >
                      {allSelected ? (
                        <SquareCheck size={18} className="text-primary" />
                      ) : (
                        <Square size={18} />
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3.5 text-left text-[11px] font-bold text-text-muted uppercase tracking-wider">Patient</th>
                  <th className="px-4 py-3.5 text-left text-[11px] font-bold text-text-muted uppercase tracking-wider">Bed</th>
                  <th className="px-4 py-3.5 text-left text-[11px] font-bold text-text-muted uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3.5 text-left text-[11px] font-bold text-text-muted uppercase tracking-wider">Payment</th>
                  <th
                    className="px-4 py-3.5 text-left text-[11px] font-bold text-text-muted uppercase tracking-wider"
                    title="Total billed amount for this admission"
                  >
                    Total
                  </th>
                  <th
                    className="px-4 py-3.5 text-left text-[11px] font-bold text-text-muted uppercase tracking-wider"
                    title="Charges recorded on this admission that are not on an invoice yet"
                  >
                    Unbilled
                  </th>
                  <th className="px-4 py-3.5 text-left text-[11px] font-bold text-text-muted uppercase tracking-wider">Due</th>
                  <th className="px-4 py-3.5 text-left text-[11px] font-bold text-text-muted uppercase tracking-wider">Paid</th>
                  <th
                    className="px-4 py-3.5 text-left text-[11px] font-bold text-text-muted uppercase tracking-wider"
                    title="Date the last invoice was settled"
                  >
                    Settled On
                  </th>
                  <th className="px-4 py-3.5 text-left text-[11px] font-bold text-text-muted uppercase tracking-wider">Admitted</th>
                  <th className="px-4 py-3.5 text-right text-[11px] font-bold text-text-muted uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {listAdmissions.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="px-5 py-12 text-center text-text-muted text-sm">
                      {hasActiveFilters ? (
                        <span>
                          No admissions match your filters.{' '}
                          <button onClick={clearFilters} className="text-primary hover:underline">
                            Clear filters
                          </button>
                        </span>
                      ) : (
                        'No admissions found'
                      )}
                    </td>
                  </tr>
                ) : (
                  listAdmissions.map((admission: any) => (
                    <tr key={admission.id} className="hover:bg-background/40 transition-colors">
                      <td className="px-4 py-3.5">
                        <button
                          onClick={() => toggleSelectOne(admission.id)}
                          className="text-text-muted hover:text-text transition-colors"
                          aria-label="Select admission"
                        >
                          {selectedIds.includes(admission.id) ? (
                            <SquareCheck size={18} className="text-primary" />
                          ) : (
                            <Square size={18} />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <User size={15} className="text-primary" />
                          </div>
                          <button
                            onClick={() =>
                              showsDetailsModal(admission)
                                ? setDetailAdmission(admission)
                                : openAdmission(admission.id)
                            }
                            className="text-left group"
                            title={
                              showsDetailsModal(admission)
                                ? isCancelledAdmission(admission)
                                  ? 'View cancelled admission details, receipt & payment'
                                  : 'View discharge details, receipt & payment'
                                : 'View patient & admission details'
                            }
                          >
                            <span className="block text-sm font-medium text-text group-hover:text-primary group-hover:underline transition-colors">
                              {getPatientName(admission.patient)}
                              {hasReferralPartner(admission) && (
                                <span
                                  className="ml-1 inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-primary/10 align-middle text-primary ring-1 ring-primary/20"
                                  title={`Referral commission${
                                    getReferralPartnerLabel(admission)
                                      ? ` — ${getReferralPartnerLabel(admission)}`
                                      : ''
                                  }${
                                    getReferralCommission(admission) > 0
                                      ? ` (${money(getReferralCommission(admission))})`
                                      : ' (no amount set)'
                                  }`}
                                  aria-label="Referral commission"
                                >
                                  <UserPlus size={10} />
                                </span>
                              )}
                            </span>
                            {admission.patient?.registrationNumber && (
                              <span className="block text-[11px] font-mono text-text-muted">
                                {admission.patient.registrationNumber}
                              </span>
                            )}
                            {getReferredFromLabel(admission) && (
                              <span className="mt-0.5 flex items-center gap-1 text-[11px] text-text-muted">
                                <FileText size={10} className="shrink-0" />
                                Referred from: {getReferredFromLabel(admission)}
                              </span>
                            )}
                            {hasReferralPartner(admission) && (
                              <span
                                className="mt-0.5 flex items-center gap-1 text-[11px] text-text-muted"
                                title={`Referral commission${
                                  getReferralPartnerLabel(admission)
                                    ? ` — ${getReferralPartnerLabel(admission)}`
                                    : ''
                                }${
                                  getReferralCommission(admission) > 0
                                    ? ` (${money(getReferralCommission(admission))})`
                                    : ' (no amount set)'
                                }`}
                              >
                                <UserPlus size={10} className="shrink-0" />
                                Commission
                                {getReferralPartnerName(admission)
                                  ? `: ${getReferralPartnerName(admission)}`
                                  : ''}
                                {getReferralCommission(admission) > 0
                                  ? ` · ${money(getReferralCommission(admission))}`
                                  : ' · no amount'}
                              </span>
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        {admission.bed ? (
                          <>
                            <div className="text-sm text-text font-medium">
                              {getBedName(admission.bed)}
                              {getWardName(admission.bed) !== '—' && (
                                <span className="text-text-muted font-normal">
                                  {' '}({getWardName(admission.bed)})
                                </span>
                              )}
                            </div>
                            {admission.bed?.price > 0 && (
                              <div className="text-[11px] text-text-muted mt-0.5">
                                ৳{Number(admission.bed.price).toLocaleString()}/day
                                {Number(admission.bedChargeCount || 0) > 0 && (
                                  <>
                                    {' '}• {Number(admission.bedChargeCount)} night
                                    {Number(admission.bedChargeCount) === 1 ? '' : 's'} billed
                                  </>
                                )}
                              </div>
                            )}
                            {getReferredByLabel(admission) && (
                              <div className="mt-0.5 flex items-center gap-1 text-[11px] text-text-muted">
                                <Stethoscope size={10} className="shrink-0" />
                                Ref By: {getReferredByLabel(admission)}
                              </div>
                            )}
                            {isEmergencyAdmission(admission) && (
                              <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">
                                <AlertTriangle size={10} />
                                Emergency
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-amber-700">
                              <AlertTriangle size={11} />
                              Emergency
                            </span>
                            {getReferredByLabel(admission) && (
                              <div className="mt-1 flex items-center gap-1 text-[11px] text-text-muted">
                                <Stethoscope size={10} className="shrink-0" />
                                Ref By: {getReferredByLabel(admission)}
                              </div>
                            )}
                          </>
                        )}
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide ${admissionStatusClass(admission.status)}`}>
                          {admission.status?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <AdmissionPaymentBadge
                          status={admission.paymentStatus}
                          title={
                            admission.paymentStatus === 'DUE'
                              ? `${admission.invoiceCount} invoice(s) — balance still unpaid`
                              : admission.paymentStatus === 'PARTIAL'
                                ? `Invoices settled, but ${admission.unbilledCount || 0} charge(s) worth ${money(
                                    Number(admission.unbilledAmount || 0),
                                  )} are not invoiced yet — create the final invoice before discharge`
                                : admission.paymentStatus === 'RISK_FUND'
                                  ? `${admission.invoiceCount} invoice(s) — outstanding balance written off to the Risk Fund`
                                  : admission.paymentStatus === 'PAID'
                                    ? `${admission.invoiceCount} invoice(s) — fully invoiced and settled`
                                    : 'No invoice has been raised for this visit yet'
                          }
                        />
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        {Number(admission.invoiceCount || 0) === 0 ? (
                          <span className="text-sm text-text-muted">—</span>
                        ) : (
                          <p className="text-sm font-semibold text-text">
                            {money(Number(admission.totalAmount || 0))}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        {Number(admission.unbilledAmount || 0) > 0 ? (
                          <p
                            className="text-sm font-semibold text-amber-700"
                            title={`${admission.unbilledCount || 0} charge(s) not invoiced yet`}
                          >
                            {money(Number(admission.unbilledAmount || 0))}
                          </p>
                        ) : (
                          <span className="text-sm text-text-muted">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        {Number(admission.invoiceCount || 0) === 0 ? (
                          <span className="text-sm text-text-muted">—</span>
                        ) : (
                          <p
                            className={`text-sm font-semibold ${
                              Number(admission.dueAmount || 0) > 0 ? 'text-danger' : 'text-text-muted'
                            }`}
                          >
                            {money(Number(admission.dueAmount || 0))}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        {Number(admission.invoiceCount || 0) === 0 ? (
                          <span className="text-sm text-text-muted">—</span>
                        ) : (
                          <p className="text-sm font-semibold text-emerald-700">
                            {money(Number(admission.paidAmount || 0))}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        {admission.settledAt ? (
                          <span className="text-sm text-text-muted">
                            {new Date(admission.settledAt).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: '2-digit',
                            })}
                          </span>
                        ) : (
                          <span className="text-sm text-text-muted">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5">
                        {/* Date only — the exact admission time lives in the
                            admission details/edit page. Hover keeps it handy. */}
                        <div
                          className="flex items-center gap-2 text-sm text-text-muted"
                          title={formatOrdinalDateTimeLong(getAdmittedAt(admission))}
                        >
                          <Calendar size={14} className="shrink-0" />
                          <span className="whitespace-nowrap">
                            {formatOrdinalDate(getAdmittedAt(admission))}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center justify-end gap-1.5">
                          {isOpenStayStatus(admission.status) && (
                            <button
                              onClick={() => handleDischarge(admission.id)}
                              disabled={dischargeAdmission.isPending && dischargeAdmission.variables?.id === admission.id}
                              className="px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg text-xs font-semibold hover:bg-emerald-100 transition-colors disabled:opacity-50 whitespace-nowrap"
                            >
                              {dischargeAdmission.isPending && dischargeAdmission.variables?.id === admission.id ? (
                                <Loader2 size={12} className="animate-spin inline" />
                              ) : (
                                <>
                                  <CheckCircle size={12} className="inline mr-1" />
                                  Discharge
                                </>
                              )}
                            </button>
                          )}
                          {isOpenStayStatus(admission.status) && canCancelAdmission(admission) && (
                            <button
                              onClick={() => setAdmissionAction({ type: 'cancel', admission })}
                              title="Cancel admission"
                              aria-label="Cancel admission"
                              className="p-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors inline-flex items-center justify-center"
                            >
                              <Ban size={14} />
                            </button>
                          )}
                          <button
                            onClick={() => setAdmissionAction({ type: 'delete', admission })}
                            title="Delete admission"
                            aria-label="Delete admission"
                            className="p-2 bg-danger/10 text-danger border border-danger/20 rounded-lg hover:bg-danger/20 transition-colors inline-flex items-center justify-center"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            totalPages={listMeta?.totalPages || 1}
            total={listMeta?.total || 0}
            limit={limit}
            onPageChange={setPage}
            itemName="admissions"
          />
        </div>
      )}

      {/* ===== BED DETAIL MODAL ===== */}
      {selectedBed && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={closeModal}>
          <div
            className="bg-surface rounded-2xl border border-border shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="relative overflow-hidden">
              <div className={`h-24 ${(getStatusMeta(selectedBed.status)).lightBg} opacity-10`} />
              <div className="absolute inset-0 flex items-end p-5">
                <div className="flex items-center gap-4 w-full">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm border-2 border-white ${(getStatusMeta(selectedBed.status)).bg}`}>
                    <BedDouble size={26} className={(getStatusMeta(selectedBed.status)).color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-bold text-text truncate">Bed {selectedBed.bedNumber}</h2>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                        selectedBed.status === 'AVAILABLE' ? 'bg-emerald-100 text-emerald-700' :
                        selectedBed.status === 'OCCUPIED' ? 'bg-rose-100 text-rose-700' :
                        selectedBed.status === 'RESERVED' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {(getStatusMeta(selectedBed.status)).label}
                      </span>
                      <span className="text-xs text-text-muted">{selectedBed.ward}</span>
                    </div>
                  </div>
                  <button onClick={closeModal} className="p-2 hover:bg-black/5 rounded-xl transition-colors">
                    <X size={18} className="text-text-muted" />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-5 space-y-5">
              {/* Bed Details */}
              <div>
                <h3 className="text-xs font-bold text-text-muted uppercase tracking-wider mb-3">Bed Information</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-background border border-border">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <MapPin size={15} className="text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] text-text-muted uppercase tracking-wide">Ward</p>
                      <p className="text-sm font-semibold text-text truncate">{selectedBed.ward || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-background border border-border">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <ClipboardList size={15} className="text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] text-text-muted uppercase tracking-wide">Type</p>
                      <p className="text-sm font-semibold text-text truncate">{selectedBed.type || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-background border border-border">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Banknote size={15} className="text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] text-text-muted uppercase tracking-wide">Price</p>
                      <p className="text-sm font-semibold text-text">৳{selectedBed.price || 0}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-background border border-border">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Hash size={15} className="text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] text-text-muted uppercase tracking-wide">ID</p>
                      <p className="text-sm font-semibold text-text font-mono">{selectedBed.id.slice(0, 8)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Occupancy Details */}
              {selectedAdmissionForBed ? (
                <div className="border-t border-border pt-5">
                  <h3 className="text-xs font-bold text-text-muted uppercase tracking-wider mb-3">Current Occupancy</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-background border border-border">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <User size={15} className="text-primary" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] text-text-muted uppercase tracking-wide">Patient</p>
                        <p className="text-sm font-semibold text-text truncate">{getPatientName(selectedAdmissionForBed.patient)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-background border border-border">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <Stethoscope size={15} className="text-primary" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] text-text-muted uppercase tracking-wide">Doctor</p>
                        <p className="text-sm font-semibold text-text truncate">{getDoctorName(selectedAdmissionForBed.doctor)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-background border border-border">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <FileText size={15} className="text-primary" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] text-text-muted uppercase tracking-wide">Reason / Diagnosis</p>
                        <p className="text-sm font-semibold text-text">{getReason(selectedAdmissionForBed)}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-3 p-3 rounded-xl bg-background border border-border">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <Calendar size={15} className="text-primary" />
                        </div>
                        <div>
                          <p className="text-[10px] text-text-muted uppercase tracking-wide">Admitted</p>
                          <p className="text-sm font-semibold text-text">
                            {formatOrdinalDateTimeLong(getAdmittedAt(selectedAdmissionForBed))}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 rounded-xl bg-background border border-border">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <Activity size={15} className="text-primary" />
                        </div>
                        <div>
                          <p className="text-[10px] text-text-muted uppercase tracking-wide">Status</p>
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${admissionStatusClass(selectedAdmissionForBed.status)}`}>
                            {selectedAdmissionForBed.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {selectedAdmissionForBed.status === 'ADMITTED' && (
                    <button
                      onClick={() => { handleDischarge(selectedAdmissionForBed.id); closeModal(); }}
                      disabled={dischargeAdmission.isPending}
                      className="w-full mt-4 px-4 py-2.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-xl text-sm font-bold hover:bg-emerald-100 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {dischargeAdmission.isPending ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <CheckCircle size={16} />
                      )}
                      Discharge Patient
                    </button>
                  )}
                </div>
              ) : (
                <div className="border-t border-border pt-5">
                  <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                      <CheckCircle size={20} className="text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-emerald-800">Bed Available</p>
                      <p className="text-xs text-emerald-600 mt-0.5">This bed is vacant and ready for a new admission.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== DISCHARGE BILLING MODAL ===== */}
      {billingGate && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          onClick={() => setBillingGate(null)}
        >
          <div
            className="bg-surface border border-border rounded-2xl shadow-xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 space-y-4">
              <div className="flex items-start gap-3">
                <div
                  className={`p-2 rounded-lg shrink-0 ${
                    gateNeedsInvoice ? 'bg-danger/10' : 'bg-warning/10'
                  }`}
                >
                  <AlertTriangle
                    size={20}
                    className={gateNeedsInvoice ? 'text-danger' : 'text-warning'}
                  />
                </div>
                <div className="space-y-1 min-w-0">
                  <h3 className="text-base font-semibold text-text">
                    {gateNeedsInvoice ? 'Final bill not created' : 'Unpaid balance'}
                  </h3>
                  <p className="text-sm text-text-muted">
                    {billingGate.admission?.patient
                      ? getPatientName(billingGate.admission.patient)
                      : 'This patient'}
                    {gateNeedsInvoice
                      ? "'s stay cannot be closed until every charge is on an invoice."
                      : "'s bill is invoiced, but the balance is still unpaid. You can discharge now and collect it later."}
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-border bg-background divide-y divide-border">
                {billingGate.status.unbilledCount > 0 && (
                  <div className="flex items-center justify-between gap-3 px-4 py-2.5">
                    <span className="text-sm text-text-muted">
                      Unbilled charges ({billingGate.status.unbilledCount})
                    </span>
                    <span className="text-sm font-semibold text-warning whitespace-nowrap">
                      {money(billingGate.status.unbilledAmount)}
                    </span>
                  </div>
                )}
                {gateDueAmount > 0 && (
                  <div className="flex items-center justify-between gap-3 px-4 py-2.5">
                    <span className="text-sm text-text-muted">Outstanding due</span>
                    <span className="text-sm font-semibold text-danger whitespace-nowrap">
                      {money(gateDueAmount)}
                    </span>
                  </div>
                )}
                {billingGate.status.advancePaid > 0 && (
                  <div className="flex items-center justify-between gap-3 px-4 py-2.5">
                    <span className="text-sm text-text-muted">
                      Advance received (held separately)
                    </span>
                    <span className="text-sm font-semibold text-text whitespace-nowrap">
                      {money(billingGate.status.advancePaid)}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <span className="text-sm font-medium text-text">
                    {gateNeedsInvoice ? 'Amount to invoice' : 'Balance to carry'}
                  </span>
                  <span className="text-base font-bold text-text whitespace-nowrap">
                    {money(
                      gateNeedsInvoice
                        ? billingGate.status.unbilledAmount
                        : gateDueAmount,
                    )}
                  </span>
                </div>
              </div>

              {gateNeedsInvoice ? (
                <p className="text-xs text-text-muted">
                  Create the invoice from the unbilled charges, then discharge again.
                  A patient can leave with an unpaid balance, but never with charges
                  that are not on an invoice.
                </p>
              ) : (
                <>
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-text-muted">
                      Promised payment date (optional)
                    </label>
                    <DatePicker
                      value={dueDate}
                      onChange={setDueDate}
                      placeholder="Select a date"
                    />
                    <input
                      type="text"
                      value={dueNote}
                      onChange={(e) => setDueNote(e.target.value)}
                      placeholder="Note (optional) — e.g. insurance claim pending"
                      className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/25"
                    />
                  </div>
                  <p className="text-xs text-text-muted">
                    The invoice stays open. Collect the balance from Billing → Invoices
                    at any time.
                    {billingGate.status.advancePaid > 0 &&
                      ' The advance above is not applied to this bill — settle it at the counter first.'}
                  </p>
                </>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border">
              <button
                type="button"
                onClick={() => setBillingGate(null)}
                className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
              >
                Close
              </button>
              {gateNeedsInvoice ? (
                canBill && (
                  <button
                    type="button"
                    onClick={() => {
                      const { status, admission } = billingGate;
                      setBillingGate(null);
                      navigate(
                        `/billing/invoices/new?patientId=${
                          admission?.patientId || status.patientId
                        }&visitId=${status.visitId}`,
                      );
                    }}
                    className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary-dark transition-colors"
                  >
                    Create Invoice
                  </button>
                )
              ) : (
                <>
                  {canBill && (
                    <button
                      type="button"
                      onClick={() => {
                        setBillingGate(null);
                        navigate('/billing/invoices');
                      }}
                      className="px-4 py-2 rounded-lg border border-border text-text text-sm font-medium hover:bg-background transition-colors"
                    >
                      Collect payment
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() =>
                      handleDischarge(billingGate.id, {
                        allowDue: true,
                        dischargeDueDate: dueDate || undefined,
                        dischargeDueNote: dueNote.trim() || undefined,
                      })
                    }
                    disabled={dischargeAdmission.isPending}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary-dark transition-colors disabled:opacity-50"
                  >
                    {dischargeAdmission.isPending ? (
                      <Loader2 size={15} className="animate-spin" />
                    ) : (
                      <CheckCircle size={15} />
                    )}
                    Discharge with due
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== SINGLE ADMISSION ACTION CONFIRM ===== */}
      <ConfirmDialog
        open={!!admissionAction}
        title={admissionAction?.type === 'delete' ? 'Delete admission?' : 'Cancel admission?'}
        description={
          <>
            {admissionAction?.type === 'delete' ? (
              <>
                This will move the admission for{' '}
                <strong className="text-text">
                  {getPatientName(admissionAction?.admission?.patient)}
                </strong>{' '}
                to the trash and free up the bed. Billing records are kept.
              </>
            ) : (
              <>
                This will mark the admission for{' '}
                <strong className="text-text">
                  {getPatientName(admissionAction?.admission?.patient)}
                </strong>{' '}
                as cancelled and free up the bed. The record stays in the list for review.
                {Number(admissionAction?.admission?.dueAmount || 0) > 0 && (
                  <>
                    {' '}
                    Its unpaid balance of{' '}
                    <strong className="text-text">
                      {money(Number(admissionAction?.admission?.dueAmount || 0))}
                    </strong>{' '}
                    will be written off and moved to the Risk Fund.
                  </>
                )}
              </>
            )}
          </>
        }
        confirmLabel={admissionAction?.type === 'delete' ? 'Delete admission' : 'Cancel admission'}
        loading={deleteAdmission.isPending || cancelAdmission.isPending}
        onConfirm={handleAdmissionAction}
        onCancel={() => setAdmissionAction(null)}
      />

      {/* ===== BULK DELETE CONFIRM ===== */}
      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Delete ${selectedIds.length} admission${selectedIds.length === 1 ? '' : 's'}?`}
        description="This will remove the selected admissions and free up any occupied beds. Active staff assignments will be closed."
        confirmLabel={`Delete ${selectedIds.length} admission${selectedIds.length === 1 ? '' : 's'}`}
        loading={bulkDeleteAdmissions.isPending}
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />

      {/* ===== BULK RISK FUND CONFIRM ===== */}
      <RiskFundMoveDialog
        open={riskFundConfirmOpen}
        title={`Move ${selectedIds.length} admission${selectedIds.length === 1 ? '' : 's'} to the Risk Fund?`}
        description="Every unpaid invoice on the selected admissions will be written off, so the balance leaves billing due and the discharge-due snapshot is cleared."
        reason={riskFundReason}
        onReasonChange={setRiskFundReason}
        error={riskFundError}
        loading={moveToRiskFund.isPending}
        confirmLabel={`Move ${selectedIds.length} admission${selectedIds.length === 1 ? '' : 's'} to Risk Fund`}
        onConfirm={handleBulkRiskFund}
        onCancel={() => {
          setRiskFundConfirmOpen(false);
          setRiskFundReason('');
          setRiskFundError(null);
        }}
      />

      {/* ===== DISCHARGED ADMISSION DETAILS, PRINT & DUE PAYMENT ===== */}
      <IpdAdmissionDetailsModal
        admission={detailAdmission}
        onClose={() => setDetailAdmission(null)}
        onViewAdmission={(id) => {
          setDetailAdmission(null);
          openAdmission(id);
        }}
        canPay={canBill}
      />
    </div>
  );
}
